import { useEffect, useState } from 'react';
import axios from 'axios';
import { ListChecks, Plus, Pencil, Trash2, X, CheckCircle2, AlertCircle, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenantScope } from '../context/TenantScopeContext';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';

interface Rule {
  id: number;
  tenant_id: number | null;
  tenant_name: string | null;
  name: string;
  keyword: string | null;
  exclude_keyword: string | null;
  sigma_yaml: string | null;
  severity: 'low' | 'medium' | 'high' | 'critical';
  mitre_tactic: string | null;
  mitre_technique: string | null;
  correlation_window_minutes: number;
  action: 'alert' | 'counter';
  enabled: boolean;
  // Soglia/sequenza (2026-09-17): null = un alert per evento.
  threshold_count: number | null;
  threshold_window_seconds: number | null;
  group_by: GroupBy;
  followed_by_keyword: string | null;
}

type GroupBy = 'host' | 'src_ip' | 'user';
const GROUP_BY_LABEL: Record<GroupBy, string> = { host: 'host', src_ip: 'source IP', user: 'user' };

function describeWindow(seconds: number): string {
  if (seconds % 3600 === 0) return `${seconds / 3600} h`;
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}

const SEVERITY_BADGE: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600', medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700',
};

const COLUMNS: ColumnDef[] = [
  { key: 'scope', label: 'Scope', defaultVisible: true },
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'mode', label: 'Mode', defaultVisible: true },
  { key: 'trigger', label: 'Trigger', defaultVisible: true },
  { key: 'severity', label: 'Severity', defaultVisible: true },
  { key: 'action', label: 'Action', defaultVisible: true },
  { key: 'mitre', label: 'MITRE', defaultVisible: true },
  { key: 'enabled', label: 'Enabled', defaultVisible: true },
  { key: 'ops', label: '', defaultVisible: true },
];

type Mode = 'keyword' | 'sigma';

interface FormState {
  id: number | null;
  name: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  mitreTactic: string;
  mitreTechnique: string;
  correlationWindowMinutes: number;
  action: 'alert' | 'counter';
  global: boolean;
  mode: Mode;
  keyword: string;
  excludeKeyword: string;
  sigmaYaml: string;
  enabled: boolean;
  trigger: 'event' | 'threshold';
  thresholdCount: number;
  thresholdWindowSeconds: number;
  groupBy: GroupBy;
  followedByKeyword: string;
}

const SIGMA_PLACEHOLDER = `detection:
  selection:
    threat|contains: ransomware
  blocked:
    action: blocked
  condition: selection and not blocked
`;

function emptyForm(defaultTenantSelected: boolean): FormState {
  return {
    id: null, name: '', severity: 'medium', mitreTactic: '', mitreTechnique: '',
    correlationWindowMinutes: 15, action: 'alert', global: !defaultTenantSelected,
    mode: 'keyword', keyword: '', excludeKeyword: '', sigmaYaml: '', enabled: true,
    trigger: 'event', thresholdCount: 10, thresholdWindowSeconds: 300, groupBy: 'host', followedByKeyword: '',
  };
}

// Gestione delle regole di detection (2026-09-12): non esisteva alcuna UI prima — le regole
// erano leggibili/modificabili solo via SQL diretto sul server (vedi CLAUDE.md). Supporta sia il
// formato storico (keyword/exclude_keyword) sia il motore Sigma reale (ingestion/sigma.ts,
// condizioni booleane per campo) introdotto nella stessa sessione, mutuamente esclusivi.
export default function DetectionRulesPage() {
  const { user, canEdit } = useAuth();
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.detectionRules', COLUMNS);
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState<{ ok: boolean; text: string } | null>(null);
  const [validating, setValidating] = useState(false);
  const [deleting, setDeleting] = useState<Rule | null>(null);

  async function load() {
    setLoading(true);
    const res = await axios.get('/api/detection-rules', { params: { tenantId: selectedTenantId ?? undefined } });
    setRules(res.data.data);
    setLoading(false);
  }

  useEffect(() => { load(); }, [selectedTenantId]);

  function openCreate() {
    setSaveError(null);
    setValidation(null);
    setForm(emptyForm(selectedTenantId !== null));
  }

  function openEdit(r: Rule) {
    setSaveError(null);
    setValidation(null);
    setForm({
      id: r.id, name: r.name, severity: r.severity, mitreTactic: r.mitre_tactic || '',
      mitreTechnique: r.mitre_technique || '', correlationWindowMinutes: r.correlation_window_minutes,
      action: r.action, global: r.tenant_id === null,
      mode: r.sigma_yaml ? 'sigma' : 'keyword',
      keyword: r.keyword || '', excludeKeyword: r.exclude_keyword || '', sigmaYaml: r.sigma_yaml || SIGMA_PLACEHOLDER,
      enabled: r.enabled,
      trigger: r.threshold_count !== null ? 'threshold' : 'event',
      thresholdCount: r.threshold_count ?? 10, thresholdWindowSeconds: r.threshold_window_seconds ?? 300,
      groupBy: r.group_by || 'host', followedByKeyword: r.followed_by_keyword || '',
    });
  }

  async function validateSigma() {
    if (!form) return;
    setValidating(true);
    setValidation(null);
    try {
      const res = await axios.post('/api/detection-rules/validate-sigma', { sigmaYaml: form.sigmaYaml });
      const { selectionNames, fieldsUsed } = res.data.data;
      setValidation({ ok: true, text: `Valid — selections: ${selectionNames.join(', ')}; fields used: ${fieldsUsed.join(', ') || 'none'}` });
    } catch (err: any) {
      setValidation({ ok: false, text: err.response?.data?.error || 'Validation failed' });
    } finally {
      setValidating(false);
    }
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    setSaveError(null);
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      severity: form.severity,
      mitreTactic: form.mitreTactic.trim() || null,
      mitreTechnique: form.mitreTechnique.trim() || null,
      correlationWindowMinutes: Number(form.correlationWindowMinutes) || 15,
      action: form.action,
      enabled: form.enabled,
      // thresholdCount sempre presente (null = per evento): è così che il PUT sa di aggiornarla.
      thresholdCount: form.trigger === 'threshold' && form.action === 'alert' ? Number(form.thresholdCount) : null,
      thresholdWindowSeconds: form.trigger === 'threshold' ? Number(form.thresholdWindowSeconds) : null,
      groupBy: form.groupBy,
      followedByKeyword: form.trigger === 'threshold' ? form.followedByKeyword.trim() || null : null,
    };
    if (form.mode === 'sigma') {
      payload.sigmaYaml = form.sigmaYaml;
    } else {
      payload.keyword = form.keyword.trim();
      payload.excludeKeyword = form.excludeKeyword.trim() || null;
    }
    try {
      if (form.id === null) {
        payload.tenantId = form.global ? null : selectedTenantId;
        await axios.post('/api/detection-rules', payload);
      } else {
        await axios.put(`/api/detection-rules/${form.id}`, payload);
      }
      setForm(null);
      await load();
    } catch (err: any) {
      setSaveError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(r: Rule) {
    await axios.put(`/api/detection-rules/${r.id}`, { enabled: !r.enabled });
    load();
  }

  async function confirmDelete() {
    if (!deleting) return;
    await axios.delete(`/api/detection-rules/${deleting.id}`);
    setDeleting(null);
    load();
  }

  const canCreateGlobal = user?.scopeLevel === 'platform';
  const canCreateHere = selectedTenantId !== null || canCreateGlobal;

  function renderCell(r: Rule, key: string) {
    switch (key) {
      case 'scope':
        return r.tenant_id === null
          ? <span className="inline-flex items-center gap-1 text-primary-700 font-medium"><Globe size={12} /> Global</span>
          : <span className="text-gray-500">{r.tenant_name}</span>;
      case 'name':
        return <span className="font-medium text-gray-900">{r.name}</span>;
      case 'mode':
        return r.sigma_yaml
          ? <span className="badge bg-violet-100 text-violet-700">Sigma</span>
          : <span className="badge bg-gray-100 text-gray-600" title={r.exclude_keyword ? `Excludes: ${r.exclude_keyword}` : undefined}>
              Keyword: {r.keyword}
            </span>;
      case 'trigger':
        if (r.action === 'counter' || r.threshold_count === null) return <span className="text-gray-400 text-xs">every event</span>;
        return (
          // Non `badge` (pillola): il testo va spesso su due righe e una pillola multi-riga diventa una macchia.
          <span className="inline-block rounded-md px-2 py-0.5 text-xs leading-snug bg-sky-100 text-sky-700" title={`Counted per ${GROUP_BY_LABEL[r.group_by]}`}>
            {r.followed_by_keyword
              ? `${r.threshold_count}× then "${r.followed_by_keyword}" in ${describeWindow(r.threshold_window_seconds!)}`
              : `≥ ${r.threshold_count} in ${describeWindow(r.threshold_window_seconds!)}`}
            {' '}per {GROUP_BY_LABEL[r.group_by]}
          </span>
        );
      case 'severity':
        return <span className={`badge ${SEVERITY_BADGE[r.severity]}`}>{r.severity}</span>;
      case 'action':
        return r.action === 'counter'
          ? <span className="badge bg-slate-100 text-slate-600">counter</span>
          : <span className="text-gray-500">alert</span>;
      case 'mitre':
        return <span className="text-gray-500 text-xs">{[r.mitre_tactic, r.mitre_technique].filter(Boolean).join(' / ') || '—'}</span>;
      case 'enabled':
        return (
          <button onClick={() => toggleEnabled(r)} className={`badge ${r.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
            {r.enabled ? 'Enabled' : 'Disabled'}
          </button>
        );
      case 'ops':
        return (
          <div className="flex items-center gap-2 justify-end">
            {canEdit && (
              <>
                <button onClick={() => openEdit(r)} className="text-gray-400 hover:text-primary-600"><Pencil size={14} /></button>
                <button onClick={() => setDeleting(r)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2">
            <ListChecks size={20} /> Custom Detection Rules
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {selectedTenantId ? `Global rules plus rules scoped to ${selectedTenantName}.` : 'Rules visible in your scope.'}{' '}
            Simple keyword rules or, for real per-field boolean conditions, a Sigma-style rule; each can alert on every event
            or only past a threshold (N events in a window, per host / source IP / user, optionally followed by a second event).
          </p>
        </div>
        <button onClick={openCreate} disabled={!canCreateHere || !canEdit} title={!canEdit ? 'Your role (viewer) cannot create rules' : !canCreateHere ? 'Select a tenant to create a rule here' : undefined}
          className="btn-primary flex items-center gap-1.5 disabled:opacity-50">
          <Plus size={14} /> New Rule
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {orderedDefs.map(d => (
                <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className="table-header">
                  {d.label}
                </DraggableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rules.length === 0 && (
              <tr><td className="table-cell text-gray-400" colSpan={orderedDefs.length}>Loading…</td></tr>
            )}
            {!loading && rules.length === 0 && (
              <tr><td className="table-cell text-gray-400" colSpan={orderedDefs.length}>No rules in this scope yet.</td></tr>
            )}
            {rules.map(r => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                {orderedDefs.map(d => <td key={d.key} className="table-cell">{renderCell(r, d.key)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">{form.id === null ? 'New detection rule' : 'Edit detection rule'}</h2>
              <button onClick={() => setForm(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-gray-600 text-xs">Name</span>
                  <input className="input-field w-full mt-1" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-gray-600 text-xs">Severity</span>
                  <select className="input-field w-full mt-1" value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value as FormState['severity'] })}>
                    <option value="low">low</option><option value="medium">medium</option>
                    <option value="high">high</option><option value="critical">critical</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-gray-600 text-xs">MITRE Tactic</span>
                  <input className="input-field w-full mt-1" value={form.mitreTactic} onChange={e => setForm({ ...form, mitreTactic: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-gray-600 text-xs">MITRE Technique</span>
                  <input className="input-field w-full mt-1" value={form.mitreTechnique} onChange={e => setForm({ ...form, mitreTechnique: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-gray-600 text-xs">Correlation window (minutes)</span>
                  <input type="number" min={1} className="input-field w-full mt-1" value={form.correlationWindowMinutes}
                    onChange={e => setForm({ ...form, correlationWindowMinutes: Number(e.target.value) })} />
                </label>
                <label className="block">
                  <span className="text-gray-600 text-xs">On match</span>
                  <select className="input-field w-full mt-1" value={form.action} onChange={e => setForm({ ...form, action: e.target.value as FormState['action'] })}>
                    <option value="alert">Open alert/case</option>
                    <option value="counter">Count only (no case) — for expected noise</option>
                  </select>
                </label>
              </div>

              {form.action === 'alert' && (
                <div className="border-t border-gray-100 pt-3 space-y-3">
                  <div className="flex gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={form.trigger === 'event'} onChange={() => setForm({ ...form, trigger: 'event' })} />
                      <span>Alert on every matching event</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={form.trigger === 'threshold'} onChange={() => setForm({ ...form, trigger: 'threshold' })} />
                      <span>Alert on a threshold (N events in a time window)</span>
                    </label>
                  </div>
                  {form.trigger === 'threshold' && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <label className="block">
                          <span className="text-gray-600 text-xs">At least (events)</span>
                          <input type="number" min={2} max={100000} className="input-field w-full mt-1" value={form.thresholdCount}
                            onChange={e => setForm({ ...form, thresholdCount: Number(e.target.value) })} />
                        </label>
                        <label className="block">
                          <span className="text-gray-600 text-xs">Within (seconds)</span>
                          <input type="number" min={10} max={86400} className="input-field w-full mt-1" value={form.thresholdWindowSeconds}
                            onChange={e => setForm({ ...form, thresholdWindowSeconds: Number(e.target.value) })} />
                        </label>
                        <label className="block">
                          <span className="text-gray-600 text-xs">Counted per</span>
                          <select className="input-field w-full mt-1" value={form.groupBy} onChange={e => setForm({ ...form, groupBy: e.target.value as GroupBy })}>
                            <option value="host">host (the device that logged it)</option>
                            <option value="src_ip">source IP (one attacker, many targets)</option>
                            <option value="user">user (password spraying)</option>
                          </select>
                        </label>
                      </div>
                      <label className="block">
                        <span className="text-gray-600 text-xs">
                          Followed by (optional): raise the alert only when, after the threshold, an event containing this text
                          arrives from the same {GROUP_BY_LABEL[form.groupBy]} — e.g. "Accepted password" after failed logins
                        </span>
                        <input className="input-field w-full mt-1" value={form.followedByKeyword} placeholder="Accepted password"
                          onChange={e => setForm({ ...form, followedByKeyword: e.target.value })} />
                      </label>
                      <p className="text-xs text-gray-500">
                        Source IP and user are taken from the normalized fields when present, otherwise from the message
                        ("from 1.2.3.4", "for admin from"); if neither is found the event is counted per host.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {form.id === null && (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.global} disabled={!canCreateGlobal}
                    onChange={e => setForm({ ...form, global: e.target.checked })} />
                  <span className={canCreateGlobal ? '' : 'text-gray-400'}>
                    Global rule (applies to every tenant on the platform){!canCreateGlobal && ' — platform admin only'}
                  </span>
                </label>
              )}
              {form.id !== null && (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />
                  <span>Enabled</span>
                </label>
              )}

              <div className="border-t border-gray-100 pt-3">
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={form.mode === 'keyword'} onChange={() => setForm({ ...form, mode: 'keyword' })} />
                    <span>Simple keyword</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={form.mode === 'sigma'} onChange={() => setForm({ ...form, mode: 'sigma', sigmaYaml: form.sigmaYaml || SIGMA_PLACEHOLDER })} />
                    <span>Advanced (Sigma-style condition)</span>
                  </label>
                </div>

                {form.mode === 'keyword' ? (
                  <div className="space-y-3">
                    <label className="block">
                      <span className="text-gray-600 text-xs">Keyword (matched case-insensitively against the event message)</span>
                      <input className="input-field w-full mt-1" value={form.keyword} onChange={e => setForm({ ...form, keyword: e.target.value })} />
                    </label>
                    <label className="block">
                      <span className="text-gray-600 text-xs">Exclude if message also contains (comma-separated, optional)</span>
                      <input className="input-field w-full mt-1" value={form.excludeKeyword} onChange={e => setForm({ ...form, excludeKeyword: e.target.value })} />
                    </label>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">
                      Real boolean conditions over event fields (host, message, src_ip, dst_ip, action, user, url, file,
                      threat, rule_name, vendor, product, severity; Windows event logs: event_id, logon_type, target_user,
                      subject_user, process, service, group, member, status, channel — default match is "contains", use{' '}
                      <code>|equals</code>, <code>|startswith</code>, <code>|endswith</code> or <code>|re</code> for others).
                    </p>
                    <textarea className="input-field w-full font-mono text-xs" rows={10} value={form.sigmaYaml}
                      onChange={e => setForm({ ...form, sigmaYaml: e.target.value })} spellCheck={false} />
                    <div className="flex items-center gap-3">
                      <button onClick={validateSigma} disabled={validating} className="btn-secondary text-xs disabled:opacity-50">
                        {validating ? 'Validating…' : 'Validate'}
                      </button>
                      {validation && (
                        <span className={`inline-flex items-center gap-1 text-xs ${validation.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                          {validation.ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} {validation.text}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {saveError && <p className="text-xs text-red-600">{saveError}</p>}
            </div>

            <div className="flex justify-end gap-2 px-5 pb-5">
              <button onClick={() => setForm(null)} className="btn-secondary">Cancel</button>
              <button onClick={save} disabled={saving || !form.name.trim()} className="btn-primary disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Delete rule</h2>
              <button onClick={() => setDeleting(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 text-sm text-gray-700">
              Delete <span className="font-medium">{deleting.name}</span>? Alerts already raised by it are kept.
            </div>
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button onClick={() => setDeleting(null)} className="btn-secondary">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
